import { Spinner } from '@delmonicos/shared-design-system-react/src/components/spinner/spinner';
import { GatewayRoleModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/roles/models/output/gateway-role.model';
import { Action } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { PermissionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/outputs/permission.model';
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';

import {
  mustResetPassword,
  roles,
  setPermissions,
} from '@app/core/authentication/authentication.slice';
import { useAppDispatch, useAppSelector } from '@app/core/hooks';
import { Permission } from '@model/permission.model';
import { cpmsBoRoleApi } from '@service/cpms-bo-role.service';

export function PermissionsProvider({
  children,
}: Readonly<PropsWithChildren>): ReactNode {
  const rolesJWT = useAppSelector(roles);
  const needResetPassword = useAppSelector(mustResetPassword);
  const dispatch = useAppDispatch();

  const [allRoles, setAllRoles] = useState<GatewayRoleModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const limit = 100;

    async function fetchPage(
      offset: number,
      aggregated: GatewayRoleModel[],
    ): Promise<GatewayRoleModel[]> {
      const result = await dispatch(
        cpmsBoRoleApi.endpoints.cpmsBoGetRolesPaginated.initiate({
          id: rolesJWT?.map((r) => r.roleId),
          offset,
          limit,
        }),
      ).unwrap();

      const merged = [...aggregated, ...result.docs];

      if (result.hasNextPage) {
        return fetchPage(offset + limit, merged);
      }
      return merged;
    }

    async function fetchAllRoles() {
      setIsLoading(true);
      const aggregated = await fetchPage(0, []);
      if (!isCancelled) {
        setAllRoles(aggregated);
        setIsLoading(false);
      }
    }

    if (needResetPassword) {
      return () => {
        isCancelled = true;
      };
    }

    if (rolesJWT?.length) {
      fetchAllRoles();
    }

    return () => {
      isCancelled = true;
    };
  }, [dispatch, rolesJWT, needResetPassword]);

  useEffect(() => {
    if (allRoles.length && allRoles.length === rolesJWT?.length) {
      const merged: Permission[] = allRoles
        .flatMap((role: GatewayRoleModel) =>
          role.permissions.map((permission: PermissionModel) =>
            permission.actions.map((action: Action) => ({
              resourceName: permission.resource,
              action,
            })),
          ),
        )
        .flat();

      const unique: Permission[] = Array.from(
        new Map(
          merged.map((p) => [`${p.resourceName}:${p.action}`, p]),
        ).values(),
      );

      dispatch(setPermissions(unique));
    }
  }, [dispatch, allRoles, rolesJWT]);

  if (isLoading) return <Spinner />;

  return children;
}
